import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import {
  HEADING_TRANSLATIONS,
  SORT_COLS,
  TABLE_COLUMNS,
} from '../TeamMembersTable/components/TeamMembersTableCells/data';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useMultiSort from 'hooks/useMultiSort';
import useTeamFilterOptions from '../../hooks/useTeamFilterOptions';
import { ASCENDING_DESCENDING_COPY, EARLIEST_LATEST_COPY } from 'models/Sort';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import useQueryParams from 'hooks/useQueryParams';
import { getHasActiveFilters, getIsArrayEmpty } from 'utils';

import './FilterControls.styles.scss';

const MULTI_SORT_DIRECTION_COPY = {
  [SORT_COLS.totalDeals]: ASCENDING_DESCENDING_COPY,
  [SORT_COLS.totalListings]: ASCENDING_DESCENDING_COPY,
  [SORT_COLS.totalContacts]: ASCENDING_DESCENDING_COPY,
  [SORT_COLS.successFee]: ASCENDING_DESCENDING_COPY,
  [SORT_COLS.ev]: ASCENDING_DESCENDING_COPY,
  [SORT_COLS.totalDataRooms]: ASCENDING_DESCENDING_COPY,
  [SORT_COLS.lastSeen]: EARLIEST_LATEST_COPY,
};

type FilterControlsProps = {
  className?: string;
  manageColumns?: React.ReactNode;
};

const FilterControls: React.FC<FilterControlsProps> = ({
  className,
  manageColumns,
}) => {
  const classes = classNames('filter-controls', className);

  const { limit, updateLimit } = useNumberOfResultsPerPage();
  const { filterOptions } = useTeamFilterOptions();

  const { members, isLoading } = useContext(EnterpriseMembersContext);

  const { params } = useQueryParams();

  const isEmpty = !isLoading && getIsArrayEmpty(members);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    TABLE_COLUMNS,
    SORT_COLS,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  return (
    <div className={classes}>
      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={multiSortValues}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      {(!isEmpty || hasActiveFilters) && (
        <div className="filter-controls__actions">
          {!isEmpty && (
            <NumberOfResultsPerPageFilter
              limit={limit}
              updateLimit={updateLimit}
            />
          )}
          <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />
          <MultiSortPanel
            ref={multiSortRef}
            options={multiSortValues}
            sortDirectionTranslations={multiSortDirectionTranslation}
          />
          {manageColumns}
        </div>
      )}
    </div>
  );
};

export default FilterControls;
